import { FC, useMemo } from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
	Box,
	CircularProgress,
	ClickAwayListener,
	List,
	ListItemButton,
	Paper,
	Popper,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CountryFlag } from "../../../../../components/common/CountryFlag";
import { CarbonSnapSearchNode } from "../../../../../datasources/carbonsnap/io/response";
import { NodeCodeType } from "../../../../../datasources/carbonsnap/models";

type GeolocationResult = {
	title: string;
	address: {
		label: string;
		countryCode: string;
		countryName: string;
		city?: string;
		street?: string;
		postalCode?: string;
		state?: string;
	};
	position: {
		lat: number;
		lng: number;
	};
	type: "geolocation";
	code?: string;
	codeType?: "geolocation";
	nodeTypeId?: number;
	nodeTypeName?: string;
	emissions?: number;
};

type AutosuggestResult = CarbonSnapSearchNode | GeolocationResult;

interface Props {
	open: boolean;
	anchorEl: HTMLElement | null;
	results: AutosuggestResult[];
	isLoading: boolean;
	searchMode: NodeCodeType;
	onSelect: (result: AutosuggestResult) => void;
	onClose: () => void;
	onTabChange: (mode: NodeCodeType) => void;
}

const AddressAutocomplete: FC<Props> = ({
	open,
	anchorEl,
	results,
	isLoading,
	searchMode,
	onSelect,
	onClose,
	onTabChange,
}) => {
	const { t } = useTranslation("feature.carbonsnap");
	const modes: NodeCodeType[] = ["network", "unlocode", "iata", "icao", "wpi"];

	// Derive selectedTab directly from searchMode prop
	const selectedTab = useMemo(() => {
		const tabIndex = modes.indexOf(searchMode);
		return tabIndex >= 0 ? tabIndex : 0;
	}, [searchMode]);

	const handleSelect = (result: AutosuggestResult) => {
		onSelect(result);
		onClose();
	};

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		onTabChange(modes[newValue]);
	};

	if (!open || !anchorEl) {
		return null;
	}

	const showTabs = searchMode !== "geolocation";

	return (
		<Popper
			open={open}
			anchorEl={anchorEl}
			placement="bottom-start"
			style={{ width: anchorEl.clientWidth }}
			sx={{
				zIndex: (theme) => theme.zIndex.modal,
			}}
		>
			<ClickAwayListener onClickAway={onClose}>
				<Paper
					elevation={8}
					sx={{
						mt: 0.5,
						maxHeight: 400,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					{showTabs && (
						<Tabs
							value={selectedTab}
							onChange={handleTabChange}
							sx={{ borderBottom: 1, borderColor: "divider" }}
							variant="scrollable"
							scrollButtons="auto"
							allowScrollButtonsMobile
						>
							<Tab label={t("autocomplete/tab/network nodes")} />
							<Tab label={t("autocomplete/tab/unlocode")} />
							<Tab label={t("autocomplete/tab/iata")} />
							<Tab label={t("autocomplete/tab/icao")} />
							<Tab label={t("autocomplete/tab/wpi")} />
						</Tabs>
					)}

					<Box sx={{ overflowY: "auto", maxHeight: 320 }}>
						{isLoading ? (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									p: 3,
								}}
							>
								<CircularProgress size={24} />
								<Typography sx={{ ml: 2 }} variant="body2" color="text.secondary">
									{t("autocomplete/loading")}
								</Typography>
							</Box>
						) : results.length === 0 ? (
							<Box sx={{ p: 2 }}>
								<Typography variant="body2" color="text.secondary">
									{t("autocomplete/no results")}
								</Typography>
							</Box>
						) : (
							<List disablePadding>
								{results.map((result, index) => (
									<ListItemButton
										key={index}
										onClick={() => handleSelect(result)}
										sx={{
											py: 1.5,
											px: 2,
											borderBottom: index < results.length - 1 ? 1 : 0,
											borderColor: "divider",
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<Box
											sx={{
												display: "flex",
												alignItems: "flex-start",
												gap: 1.5,
												flex: 1,
												minWidth: 0,
											}}
										>
											{result.type === "geolocation" && (
												<LocationOnIcon
													sx={{ color: "primary.main", fontSize: 20, mt: 0.2, flexShrink: 0 }}
												/>
											)}
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography variant="body2" sx={{ fontWeight: "medium" }}>
													{result.address.label}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{result.code ? `${result.code} - ` : ""}
													{result.position.lat.toFixed(6)}, {result.position.lng.toFixed(6)}
												</Typography>
											</Box>
										</Box>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
												ml: 2,
												flexShrink: 0,
											}}
										>
											<CountryFlag
												countryCode={result.address.countryCode}
												width={24}
												height={18}
											/>
											<Typography
												variant="caption"
												sx={{
													fontWeight: "bold",
													color: "text.secondary",
												}}
											>
												{result.address.countryCode}
											</Typography>
										</Box>
									</ListItemButton>
								))}
							</List>
						)}
					</Box>
				</Paper>
			</ClickAwayListener>
		</Popper>
	);
};

export default AddressAutocomplete;
